import { useEffect, useState, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getUsers } from '../../services/userService'
import { getBranches } from '../../services/branchService'
import { getRoles } from '../../services/referenceService'
import type { UserResponse, BranchResponse, Role } from '../../types'
import UserFormModal from '../../components/UserForm/UserFormModal'
import './UsersPage.css'

export default function UsersPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()

  const [users, setUsers] = useState<UserResponse[]>([])
  const [branches, setBranches] = useState<BranchResponse[]>([])
  const [roles, setRoles] = useState<Role[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)

  const branchId = searchParams.get('branchId') || ''
  const role = searchParams.get('role') || ''
  const archived = searchParams.get('archived') === 'true'

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getUsers({
        branchId: branchId || undefined,
        role: role || undefined,
        archived: archived || undefined,
      })
      setUsers(data)
    } catch (err) {
      console.error('[ERROR] Failed to load users', err)
    } finally {
      setLoading(false)
    }
  }, [branchId, role, archived])

  useEffect(() => {
    load()
    getBranches().then(setBranches).catch(() => {})
    getRoles().then(setRoles).catch(() => {})
  }, [load])

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(searchParams)
    if (value) next.set(key, value)
    else next.delete(key)
    setSearchParams(next)
  }

  return (
    <div className="users-page">
      <div className="users-page__header">
        <h1 className="users-page__title">{t('admin.users.title')}</h1>
        <button className="admin-btn admin-btn--primary" onClick={() => setShowCreate(true)}>
          {t('admin.users.new_user')}
        </button>
      </div>

      <div className="users-page__filters">
        <select className="admin-select" value={branchId} onChange={(e) => setParam('branchId', e.target.value)}>
          <option value="">{t('admin.users.all_branches')}</option>
          {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>

        <select className="admin-select" value={role} onChange={(e) => setParam('role', e.target.value)}>
          <option value="">{t('admin.users.all_roles')}</option>
          {roles.map((r) => <option key={r.id} value={r.name}>{r.name}</option>)}
        </select>

        <label className="admin-checkbox">
          <input
            type="checkbox"
            checked={archived}
            onChange={(e) => setParam('archived', e.target.checked ? 'true' : '')}
          />
          {t('admin.users.show_archived')}
        </label>
      </div>

      <div className="admin-table">
        <div className="admin-table__head users-page__cols">
          <span>{t('admin.users.col_name')}</span>
          <span>{t('admin.users.col_login')}</span>
          <span>{t('admin.users.col_email')}</span>
          <span>{t('admin.users.col_branch')}</span>
          <span>{t('admin.users.col_role')}</span>
          <span>{t('admin.users.col_status')}</span>
          <span>{t('admin.users.col_created')}</span>
        </div>

        {loading ? (
          <div className="admin-table__empty"><div className="admin-spinner" /></div>
        ) : users.length === 0 ? (
          <div className="admin-table__empty">{t('admin.users.no_users')}</div>
        ) : (
          users.map((u) => (
            <div
              key={u.id}
              className={`admin-table__row users-page__cols${u.archivedAt ? ' admin-table__row--archived' : ''}`}
              onClick={() => navigate(`/admin/users/${u.id}`)}
            >
              <span className="admin-table__primary">{u.firstName} {u.lastName}</span>
              <span className="admin-table__secondary">{u.login}</span>
              <span className="admin-table__secondary">{u.email}</span>
              <span className="admin-table__secondary">{u.branchName}</span>
              <span><RoleBadge role={u.role} /></span>
              <span><UserStatusDot verified={u.verified} archived={!!u.archivedAt} /></span>
              <span className="admin-table__secondary">{formatDate(u.createdAt)}</span>
            </div>
          ))
        )}
      </div>

      {showCreate && (
        <UserFormModal
          branches={branches}
          roles={roles}
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); load() }}
        />
      )}
    </div>
  )
}

export function RoleBadge({ role }: { role: string }) {
  const color = role === 'ADMIN' ? '#a5b4fc' : '#8b8fa8'
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 20,
      fontSize: 11, fontWeight: 600, background: `${color}1a`, color,
    }}>
      {role}
    </span>
  )
}

export function UserStatusDot({ verified, archived }: { verified: boolean; archived: boolean }) {
  const { t } = useTranslation()
  const color = archived ? '#5a5d72' : verified ? '#10b981' : '#f59e0b'
  const label = archived ? t('admin.users.status_archived') : verified ? t('admin.users.status_active') : t('admin.users.status_deactivated')
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />
      {label}
    </span>
  )
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}
